import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { api } from "../../../commons/axiosReq";
import { uploadAssetUrl, repairUploadUrl } from "../../../commons/uploadAssetUrl";
import { apiAsset } from "../../../commons/inFormTypes";

const BlockEmbed = ReactQuill.Quill.import("blots/block/embed");
class ArticleVideo extends BlockEmbed {
  static create(value) {
    const node = super.create();
    node.setAttribute("controls", "");
    node.setAttribute("preload", "metadata");
    if (/^https?:\/\//i.test(value)) node.setAttribute("src", value);
    return node;
  }
  static value(node) {
    return (
      node.getAttribute("src") ||
      node.querySelector("source")?.getAttribute("src") ||
      ""
    );
  }
}
ArticleVideo.blotName = "articleVideo";
ArticleVideo.tagName = "VIDEO";
ReactQuill.Quill.register(ArticleVideo, true);

export const assetUrl = (path) =>
  uploadAssetUrl(path, apiAsset);

export default function ArticleRichEditor({
  value,
  onChange,
  disabled,
  onPendingChange,
  onPreview,
  preferSource = false,
}) {
  const editor = useRef(null);
  const wrapper = useRef(null);
  const file = useRef(null);
  const source = useRef(null);
  const sourceSelection = useRef({ start: 0, end: 0 });
  const [linkText, setLinkText] = useState("");
  const position = useRef(0);
  const [mode, setMode] = useState(() =>
    preferSource || /<(table|iframe|object)\b/i.test(value || "")
      ? "source"
      : "visual",
  );
  const [media, setMedia] = useState(null);
  const [url, setUrl] = useState("");
  const [alt, setAlt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    onPendingChange?.(Boolean(media) || busy);
    return () => onPendingChange?.(false);
  }, [media, busy, onPendingChange]);
  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [2, 3, 4, false] }],
        ["bold", "italic", "underline", "blockquote"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ direction: "rtl" }, { align: [] }],
        ["link", "clean"],
      ],
    }),
    [],
  );
  useEffect(() => {
    if (mode !== "visual") return;
    const labels = {
      bold: "پررنگ",
      italic: "مورب",
      underline: "زیرخط",
      blockquote: "نقل‌قول",
      list: "فهرست",
      direction: "جهت راست به چپ",
      align: "تراز متن",
      link: "درج لینک",
      clean: "پاک‌کردن قالب‌بندی",
    };
    wrapper.current
      ?.querySelectorAll(".ql-toolbar button")
      .forEach((button) => {
        const key = [...button.classList]
          .find((name) => name.startsWith("ql-"))
          ?.slice(3);
        const label =
          key === "list"
            ? button.value === "ordered"
              ? "فهرست شماره‌دار"
              : "فهرست نشانه‌دار"
            : labels[key];
        if (label) {
          button.setAttribute("aria-label", label);
          button.title = label;
        }
      });
    wrapper.current
      ?.querySelector(".ql-editor")
      ?.setAttribute("aria-label", "متن اصلی مقاله");
  }, [mode]);
  const openMedia = (kind) => {
    if (mode === "source") {
      const textarea = source.current;
      sourceSelection.current = { start: textarea.selectionStart, end: textarea.selectionEnd };
      setLinkText((value || "").slice(textarea.selectionStart, textarea.selectionEnd));
    }
    position.current =
      editor.current?.getEditor().getSelection()?.index ??
      Math.max(0, (editor.current?.getEditor().getLength() || 1) - 1);
    setMedia(kind);
    setUrl("");
    setAlt("");
    setError("");
  };
  const upload = async (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    if (
      !/^image\/(jpeg|png|webp|gif)$/.test(selected.type) ||
      selected.size > 1000000
    ) {
      setError(
        "تصویر JPG، PNG، WebP یا GIF با حجم حداکثر یک مگابایت انتخاب کنید.",
      );
      return;
    }
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.append("FileDetails", selected);
      const result = await api.post("Article/UploadPhoto", form);
      if (typeof result.data !== "string" || !result.data.trim())
        throw new Error("پاسخ بارگذاری معتبر نیست.");
      setUrl(assetUrl(result.data));
    } catch (error) {
      setError("بارگذاری تصویر انجام نشد. دوباره تلاش کنید.");
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  };
  const insert = () => {
    if (disabled || busy) return;
    const address = url.trim();
    const validUrl = /^https?:\/\/[^\s]+$/i.test(address) ||
      (media === "link" && /^(?:\/(?![\/\\])|#)[^\s\\]+$/.test(address));
    if (!validUrl) {
      setError("یک نشانی معتبر با https:// یا http:// وارد کنید.");
      return;
    }
    if (media === "image" && !alt.trim()) {
      setError("توضیح کوتاهی برای تصویر بنویسید.");
      return;
    }
    if (mode === "source") {
      if (media === "link" && !linkText.trim()) {
        setError("متن لینک را انتخاب کنید یا اینجا بنویسید.");
        return;
      }
      const node = document.createElement(media === "image" ? "img" : "a");
      if (media === "image") {
        node.setAttribute("src", repairUploadUrl(address, apiAsset));
        node.setAttribute("alt", alt.trim());
      } else {
        node.setAttribute("href", address);
        node.setAttribute("style", "text-decoration: none; color: #1e3a8a;");
        node.textContent = linkText;
      }
      const { start, end } = sourceSelection.current;
      const html = node.outerHTML;
      const current = value || "";
      onChange(current.slice(0, start) + html + current.slice(end));
      setMedia(null);
      requestAnimationFrame(() => {
        source.current?.focus();
        source.current?.setSelectionRange(start + html.length, start + html.length);
      });
      return;
    }
    const quill = editor.current.getEditor();
    if (media === "image") {
      const img = document.createElement("img");
      img.src = repairUploadUrl(url.trim(), apiAsset);
      img.alt = alt.trim();
      quill.clipboard.dangerouslyPasteHTML(
        position.current,
        img.outerHTML,
        "user",
      );
    } else
      quill.insertEmbed(position.current, "articleVideo", url.trim(), "user");
    setMedia(null);
  };
  const pasteSourceHtml = (event) => {
    const html = event.clipboardData?.getData("text/html");
    if (!html || !/<(?:table|thead|tbody|tr|td|th|h[1-6]|p|ul|ol|img|a)\b/i.test(html)) return;
    event.preventDefault();
    const textarea = event.currentTarget;
    const currentValue = value || "";
    const start = textarea.selectionStart ?? currentValue.length;
    const end = textarea.selectionEnd ?? start;
    const next = currentValue.slice(0, start) + html + currentValue.slice(end);
    onChange(next);
    requestAnimationFrame(() => textarea.setSelectionRange(start + html.length, start + html.length));
  };
  return (
    <div className="article-rich-editor" ref={wrapper}>
      <div className="article-editor-tools">
        <div className="article-segment">
          <button
            type="button"
            disabled={disabled || Boolean(media)}
            aria-pressed={mode === "visual"}
            onClick={() => setMode("visual")}
          >
            ویرایش دیداری
          </button>
          <button
            type="button"
            disabled={disabled || Boolean(media)}
            aria-pressed={mode === "source"}
            onClick={() => setMode("source")}
          >
            ورود مستقیم HTML
          </button>
        </div>
        <div>
          <button
            type="button"
            disabled={disabled || Boolean(media)}
            onClick={() => openMedia("image")}
          >
            + تصویر
          </button>
          {mode === "visual" ? (
            <button
              type="button"
              disabled={disabled || Boolean(media)}
              onClick={() => openMedia("video")}
            >
              + ویدیو
            </button>
          ) : (
            <button
              type="button"
              disabled={disabled || Boolean(media)}
              onClick={() => openMedia("link")}
            >
              + لینک
            </button>
          )}
        </div>
      </div>
      {mode === "visual" ? (
        <ReactQuill
          ref={editor}
          theme="snow"
          value={value || ""}
          readOnly={disabled}
          modules={modules}
          onChange={(html, delta, source) => {
            if (source === "user") onChange(html);
          }}
          placeholder="مقاله را با یک مقدمه روشن شروع کنید…"
        />
      ) : (
        <>
          <div className="article-editor-tools">
            <strong>HTML مقاله را اینجا وارد کنید</strong>
            {onPreview && (
              <button type="button" onClick={onPreview}>
                پیش‌نمایش HTML
              </button>
            )}
          </div>
          <p className="article-hint">
            برای درج تصویر، محل موردنظر را در متن کد کلیک کنید؛ برای لینک‌دادن، کلمه یا عبارت را انتخاب کنید و دکمهٔ «+ لینک» را بزنید.
            کد محتوای مقاله را مستقیماً بچسبانید؛ نیازی به تگ‌های html، head و body نیست.
            اگر محتوا را از صفحهٔ دیگری کپی می‌کنید، همین حالت را نگه دارید تا جدول و قالب‌بندی HTML حفظ شود.
            این کد به‌عنوان متن مقاله ذخیره می‌شود.
            برای حفظ جدول و ساختارهای پیشرفته، کد HTML را همین‌جا ویرایش کنید.
            ویرایش دیداری ممکن است این ساختارها را ساده کند؛ پیش‌نمایش را پیش از
            ذخیره بررسی کنید.
          </p>
          <textarea
            ref={source}
            aria-label="کد HTML مقاله"
            className="article-source"
            dir="ltr"
            placeholder={'<h2>عنوان بخش</h2>\n<p>متن مقاله شما...</p>'}
            value={value || ""}
            disabled={disabled || Boolean(media)}
            onChange={(event) => onChange(event.target.value)}
            onPaste={pasteSourceHtml}
            spellCheck={false}
          />
        </>
      )}
      {media && (
        <div
          className="article-media"
          role="dialog"
          aria-label={media === "image" ? "درج تصویر" : media === "link" ? "درج لینک" : "درج ویدیو"}
        >
          <strong>
            {media === "image" ? "تصویر داخل مقاله" : media === "link" ? "لینک داخل مقاله" : "ویدیو داخل مقاله"}
          </strong>
          <label>
            نشانی {media === "image" ? "تصویر" : media === "link" ? "صفحهٔ مقصد" : "فایل ویدیو (MP4)"}
            <input
              autoFocus
              dir="ltr"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="https://…"
            />
          </label>
          {media === "link" && (
            <label>
              متن لینک
              <input value={linkText} onChange={(event) => setLinkText(event.target.value)} placeholder="کلمه یا عبارت موردنظر" />
            </label>
          )}
          {media === "image" && (
            <>
              <label>
                توضیح جایگزین تصویر
                <input
                  value={alt}
                  onChange={(event) => setAlt(event.target.value)}
                  placeholder="چه چیزی در تصویر دیده می‌شود؟"
                />
              </label>
              <input
                ref={file}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={upload}
                hidden
              />
              <button
                type="button"
                disabled={disabled || busy}
                onClick={() => file.current.click()}
              >
                {busy ? "در حال بارگذاری…" : "انتخاب تصویر از دستگاه"}
              </button>
            </>
          )}
          {error && (
            <p role="alert" className="article-error">
              {error}
            </p>
          )}
          <div className="article-button-row">
            <button
              type="button"
              className="article-primary"
              disabled={disabled || busy}
              onClick={insert}
            >
              درج در متن
            </button>
            <button
              type="button"
              disabled={disabled || busy}
              onClick={() => setMedia(null)}
            >
              انصراف
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
